import { useCallback, useState, FC } from "react";

import { Alert, Button, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useMutation } from "@tanstack/react-query";
import { setMinutes, setHours } from "date-fns";
import { AlertTriangle } from "lucide-react";
import { DateTime } from "luxon";
import { Badge } from "primereact/badge";
import { confirmDialog } from "primereact/confirmdialog";
import { Tooltip } from "primereact/tooltip";

import {
  AppointmentStatus,
  AppointmentStatusSlug,
} from "@/_constants/appointment.ts";
import {
  getServiceOrderTypeValuesByType,
  ServiceOrderType,
} from "@/_constants/serviceOrder.ts";
import { TenantType } from "@/_constants/tenantType.ts";
import ColoredMarker from "@/common/components/ColoredMarker";
import SelectForm from "@/common/components/form/SelectForm.tsx";
import SelectCreatable from "@/common/components/SelectCreatable";
import ServiceManagedByComponent from "@/common/components/ServiceManagedByComponent.tsx";
import { FormType } from "@/common/enums/sharedEnums.ts";
import DwellingInterventionHistoryTableComponent from "@/modules/dwelling/components/sheet/DwellingInterventionHistoryTableComponent.tsx";
import { ORDER_DIAGNOSTIC_EDIT } from "@/modules/order/routes";
import { listOperatorAvailability } from "@/modules/serviceOrdering/services";
import { useServiceInventoryStore } from "@/modules/serviceOrdering/store/ServiceInventoryStore.ts";

import history from "../../../routes/history.ts";
import { IServiceAgency } from "../../dwelling/reducers";
import { DWELLING_DETAILS_ESTIMATE } from "../../dwelling/routes/index.ts";
import { IUserMember } from "../../myTeam/reducers";
import { MY_ORDERS } from "../../orders/routes";
import {
  DiagnosticType,
  History,
  IPerson,
} from "../../serviceOrdering/reducers";
import {
  useDeleteService,
  useUpdateServiceAppointment,
  useUpdateServiceOperator,
  useUpdateServiceOrderedBy,
  useUpdateServiceStatus,
} from "../hooks/service.hooks.ts";
import { formTypeText } from "../props/formTypeText";

interface IOrderPreviewProps {
  isAutomaticAssignation: boolean;
  agencyAdmins?: { label: string; value: string }[];
  createdByUser?: IUserMember;
  orderedByUser?: IUserMember;
  isManagedByMainGroup: boolean;
  datedReference?: string;
  type: ServiceOrderType;
  serviceId: string;
  operator?: IUserMember;
  operators?: { label: string; value: string }[];
  diagnosticTypes?: DiagnosticType[];
  slug: string;
  appointment: {
    startDate: string;
    endDate?: string;
  };
  people: IPerson[];
  agency: IServiceAgency;
  histories: History[];
  hasSuperAdminPermission: boolean;
  dwellingId: string;
  hasReport?: boolean;
}

// Form type definition
interface OrderFormData {
  operator: { label: string; value: string } | null;
  slug: string;
  startDate: string | null;
  endDate: string | null;
  orderedByUser?: string;
}

const OrderPreview: FC<IOrderPreviewProps> = ({
  isAutomaticAssignation,
  agencyAdmins,
  createdByUser,
  orderedByUser,
  isManagedByMainGroup,
  datedReference,
  type,
  serviceId,
  diagnosticTypes,
  operator,
  operators,
  slug,
  appointment,
  people,
  agency,
  hasSuperAdminPermission,
  dwellingId,
  hasReport,
}) => {
  const { getStartDate } = useServiceInventoryStore((state) => state);
  const startDate = appointment.startDate
    ? DateTime.fromJSDate(new Date(appointment.startDate)).toISO()
    : null;
  const endDate = appointment?.endDate
    ? DateTime.fromJSDate(new Date(appointment.endDate)).toISO()
    : appointment.startDate
      ? DateTime.fromJSDate(new Date(appointment.startDate)).toISO()
      : null;

  const { mutate: updateServiceOrderedBy } = useUpdateServiceOrderedBy({
    handleSuccess: () => {
      toast.success("Commanditaire modifié avec succès");
    },
    handleError: () => {
      toast.error(formTypeText[FormType.UPDATE].error);
    },
  });

  const { mutate: updateServiceOperator } = useUpdateServiceOperator({
    handleSuccess: () => {
      toast.success("Opérateur modifié avec succès");
    },
    handleError: () => {
      toast.error(
        "Impossible de modifier l'opérateur de cette commande.\nVeuillez contacter votre administrateur système."
      );
    },
  });

  const { mutate: updateServiceStatus } = useUpdateServiceStatus({
    handleSuccess: () => {
      toast.success("Le statut a été modifié avec succès");
    },
    handleError: () => {
      toast.error(formTypeText[FormType.UPDATE].error);
    },
  });

  const { mutate: updateServiceAppointment } = useUpdateServiceAppointment({
    handleSuccess: () => {
      toast.success("La date du rendez-vous a été modifiée avec succès");
    },
    handleError: () => {
      toast.error(
        "Erreur lors de le modification du rendez-vous.\nVeuillez contacter votre administrateur système"
      );
    },
  });

  const { mutate: deleteService } = useDeleteService({
    handleSuccess: () => {
      toast.success("La commande a été supprimée avec succès");
      history.push(MY_ORDERS);
    },
    handleError: () => {
      toast.error(
        "Impossible de supprimer votre commande.\nVeuillez contacter votre administrateur système."
      );
    },
  });

  const {
    handleSubmit,
    control,
    formState: { dirtyFields, isDirty, isSubmitting },
    getValues,
    setValue,
    reset,
    watch,
  } = useForm<OrderFormData>({
    defaultValues: {
      operator: operators
        ? operators.find((item) => item.value === operator?.id)
        : null,
      slug,
      startDate,
      endDate,
      orderedByUser: orderedByUser?.id,
    },
  });

  const [isOperatorAvailabilityVisible, setIsOperatorAvailabilityVisible] =
    useState(false);

  const isOutgoingService =
    type === ServiceOrderType.OUTGOING_INVENTORY ||
    type === ServiceOrderType.OUTGOING_INVENTORY_WITH_COSTING ||
    type === ServiceOrderType.OUTGOING_INCOMING_INVENTORY;

  const operatorAvailability = useMutation(
    () =>
      listOperatorAvailability({
        dwellingId: dwellingId,
        isManagedByMainGroup: isManagedByMainGroup ?? false,
        startDate: getStartDate(
          type,
          watch("startDate") ?? undefined,
          people[0].lastTenant.leaseEndDate,
          people[0].lastTenant.leaseStartDate
        ),
        type: type,
      }),
    {
      retry: 1,
    }
  );

  const onSubmit = async (data: OrderFormData) => {
    if (dirtyFields?.orderedByUser) {
      updateServiceOrderedBy({
        serviceId,
        userId: data?.orderedByUser || "",
      });
    }

    if (dirtyFields?.operator) {
      updateServiceOperator({
        serviceId,
        operatorId: data.operator?.value || "",
      });
    }

    if (dirtyFields?.startDate || dirtyFields?.endDate) {
      updateServiceAppointment({
        serviceId,
        startDate: data.startDate || "",
        endDate: data.endDate || "",
      });
    }

    if (dirtyFields?.slug) {
      updateServiceStatus({
        serviceId,
        slug: data?.slug || "",
      });
    }

    reset(data);
  };
  const minDate = () => {
    const now = new Date();
    let minDate = new Date();
    for (const person of people) {
      const lastTenant = person?.lastTenant;
      if (lastTenant?.type === TenantType.incoming) {
        minDate = new Date(lastTenant.leaseStartDate);
        break;
      }
      if (lastTenant?.type === TenantType.outgoing) {
        const leaseEndDate = new Date(lastTenant.leaseEndDate);
        const leaseEndDateMinus = DateTime.fromJSDate(leaseEndDate)
          .plus({ day: 4 })
          .toJSDate();
        if (leaseEndDateMinus > now || leaseEndDateMinus > now) {
          minDate = now;
        } else {
          minDate = leaseEndDateMinus;
        }
        break;
      }
    }
    return minDate;
  };

  // Inside your component
  const handleDeletionConfirm = useCallback(() => {
    confirmDialog({
      message: `Voulez-vous vraiment supprimer la commande ?`,
      header: "Confirmation de suppression la commande",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Oui",
      rejectLabel: "Non",
      accept: () => deleteServiceHandler(),
    });
  }, []);

  const handleOperatorAvailabilityClick = () => {
    if (isOperatorAvailabilityVisible) {
      operatorAvailability.reset();
    } else {
      operatorAvailability.mutate();
    }
    setIsOperatorAvailabilityVisible(!isOperatorAvailabilityVisible);
  };

  const deleteServiceHandler = async () => {
    if (!hasSuperAdminPermission) {
      return;
    }
    deleteService(serviceId);
  };

  return (
    <>
      <div className="d-flex flex-row">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="orderPage-preview d-flex flex-column gap-3"
        >
          <div className="d-flex align-items-center gap-3">
            <label>Créée par :</label>
            <span>{createdByUser?.fullName ?? "Inconnu"}</span>
          </div>
          <div className="d-flex align-items-center gap-3">
            <label>Commandée par :</label>
            <div style={{ minWidth: "350px" }}>
              <Controller
                control={control}
                name={"orderedByUser"}
                render={({ field }) => {
                  return (
                    <SelectCreatable
                      options={agencyAdmins ?? []}
                      getValue={(data: { value: string; label: string }) =>
                        field.onChange(data.value)
                      }
                      isMulti={false}
                      creatable={false}
                      defaultValue={
                        field.value 
                          ? {
                              value: field.value,
                              label: agencyAdmins?.find(
                                (admin) => admin.value === field.value
                              )?.label || createdByUser?.fullName,
                            }
                          : null
                      }
                    />
                  );
                }}
              />
            </div>
          </div>
          {(!watch("orderedByUser") || watch("orderedByUser") === "") && (
            <Alert variant="warning" className="d-flex align-items-center gap-2 max-w-sm p-1" style={{ marginLeft: "156px" }}>
              <AlertTriangle className="h-4 w-4" />
              <div className='text-sm'>
                <strong>Attention :</strong> Ce champ doit être rempli pour que la messagerie fonctionne correctement.
              </div>
            </Alert>
          )}
          <div className="d-flex align-items-center gap-3">
            <label>Gestionnaire :</label>
            <div className={"d-flex flex-wrap gap-3"}>
              <ServiceManagedByComponent
                isManagedByMainGroup={isManagedByMainGroup}
              />
              <ColoredMarker className={"text-nowrap"} color={"#000D5B"}>
                {agency.label}
              </ColoredMarker>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <label>Dossier/Commande n° :</label>
            <span>{datedReference ?? "-"}</span>
          </div>
          <div className="d-flex align-items-center gap-3">
            <label>Type :</label>
            <div className={"d-flex flex-wrap gap-3"}>
              <ColoredMarker className={"text-nowrap"} color={"#13bfa4"}>
                {
                  getServiceOrderTypeValuesByType(type as keyof object)
                    ?.labelMin
                }
              </ColoredMarker>
              {type === ServiceOrderType.DIAGNOSTIC &&
                diagnosticTypes?.map((diagnosticType) => {
                  return (
                    <ColoredMarker
                      className={"text-nowrap"}
                      key={diagnosticType.slug}
                      color={"#000D5B"}
                    >
                      {diagnosticType.name}
                    </ColoredMarker>
                  );
                })}
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            {isAutomaticAssignation ? (
              <>
                <Tooltip target=".operator-info" />
                <label
                  className="operator-info"
                  data-pr-tooltip={
                    operator?.id
                      ? "Un opérateur a été assigné automatiquement."
                      : "Aucun opérateur n'a pu être assigné automatiquement."
                  }
                  data-pr-position="bottom"
                  style={{ cursor: "pointer" }}
                >
                  <Badge
                    severity={operator?.id ? "info" : "warning"}
                    className="mb-1"
                  ></Badge>
                  &nbsp;Opérateur :
                </label>
              </>
            ) : (
              <label>Opérateur :</label>
            )}
            <div style={{ width: "350px" }}>
              <SelectForm
                isClearable={true}
                extraSelect={
                  <Button
                    variant={
                      isOperatorAvailabilityVisible
                        ? "primary"
                        : "outline-secondary"
                    }
                    className="align-items-end me-1"
                    onClick={handleOperatorAvailabilityClick}
                  >
                    {operatorAvailability.isLoading ? (
                      <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      <i className="pi pi-calendar" />
                    )}
                  </Button>
                }
                defaultValue={
                  operators &&
                  operators.find((item) => item.value === operator?.id)
                }
                name={"operator"}
                options={
                  (operatorAvailability?.data?.data
                    ? operatorAvailability?.data?.data
                    : operators) ?? []
                }
                control={control as any}
                isGroup={!!operatorAvailability?.data?.data}
              />
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <label>Status :</label>
            <div style={{ minWidth: "350px" }}>
              <Controller
                control={control}
                name={"slug"}
                render={({ field }) => {
                  return (
                    <SelectCreatable
                      options={Object.keys(AppointmentStatusSlug).map(
                        (slug) => {
                          return {
                            value: slug,
                            label:
                              AppointmentStatus[
                                slug as keyof typeof AppointmentStatus
                              ],
                          };
                        }
                      )}
                      getValue={(data: { value: string; label: string }) =>
                        field.onChange(data.value)
                      }
                      isMulti={false}
                      creatable={false}
                      defaultValue={{
                        value: field.value,
                        label:
                          AppointmentStatus[
                            field.value as keyof typeof AppointmentStatus
                          ],
                      }}
                    />
                  );
                }}
              />
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <label>Début du rendez-vous :</label>
            <div
              style={{ minWidth: "350px" }}
              className="serviceOrdering_appointment"
            >
              <Controller
                control={control}
                name={"startDate"}
                render={({ field }) => {
                  return (
                    <DatePicker
                      className={`w-100 form-input_field date-select`}
                      dateFormat="'le' dd/MM/yyyy 'à' HH'h'mm"
                      locale="fr"
                      selectsStart
                      showTimeSelect
                      timeCaption="Heure :"
                      timeFormat="HH:mm"
                      timeIntervals={30}
                      minTime={setHours(setMinutes(new Date(), 0), 8)}
                      maxTime={setHours(setMinutes(new Date(), 0), 19)}
                      minDate={minDate()}
                      onChange={(date) => {
                        if (date) {
                          const value = DateTime.fromJSDate(date).toISO();
                          field.onChange(value);
                          const endDateValue = getValues("endDate");
                          if (
                            endDateValue &&
                            date.getTime() > new Date(endDateValue).getTime()
                          ) {
                            setValue(
                              "endDate",
                              DateTime.fromJSDate(date)
                                .plus({ minutes: 30 })
                                .toISO()
                            );
                          }
                        }
                      }}
                      selected={field.value ? new Date(field.value) : null}
                      startDate={field.value ? new Date(field.value) : null}
                      endDate={
                        getValues("endDate")
                          ? new Date(getValues("endDate")!)
                          : null
                      }
                    />
                  );
                }}
              />
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <label>Fin du rendez-vous :</label>
            <div style={{ minWidth: "350px" }}>
              <Controller
                control={control}
                name={"endDate"}
                render={({ field }) => (
                  <DatePicker
                    className={`w-100 form-input_field date-select`}
                    dateFormat="'le' dd/MM/yyyy 'à' HH'h'mm"
                    locale="fr"
                    selectsStart
                    showTimeSelect
                    timeCaption="Heure :"
                    timeFormat="HH:mm"
                    timeIntervals={30}
                    minTime={setHours(setMinutes(new Date(), 0), 8)}
                    maxTime={setHours(setMinutes(new Date(), 0), 19)}
                    onChange={(date) => {
                      if (date) {
                        const value = DateTime.fromJSDate(date).toISO();
                        field.onChange(value);
                      }
                    }}
                    filterTime={(time) => {
                      const startDateValue = getValues("startDate");
                      if (!startDateValue) return true;
                      const currentDate = new Date(startDateValue);
                      const selectedDate = new Date(time);

                      return currentDate.getTime() < selectedDate.getTime();
                    }}
                    selected={field.value ? new Date(field.value) : null}
                    minDate={
                      getValues("startDate")
                        ? new Date(getValues("startDate")!)
                        : undefined
                    }
                    startDate={
                      getValues("startDate")
                        ? new Date(getValues("startDate")!)
                        : undefined
                    }
                    endDate={field.value ? new Date(field.value) : null}
                  />
                )}
              />
            </div>
          </div>
          <div>
            {isOutgoingService && (
              <Button
                variant="success"
                size="md"
                className="me-2"
                onClick={() => {
                  history.push(
                    DWELLING_DETAILS_ESTIMATE.replace(":id", dwellingId)
                  );
                }}
              >
                <i className="pi pi-calculator me-2" />
                Chiffrage
              </Button>
            )}
            {[
              ServiceOrderType.DIAGNOSTIC,
              ServiceOrderType.PPPT,
              ServiceOrderType.CONDOMINIUM_VISIT,
              ServiceOrderType.CONDOMINIUM_CREATION,
              ServiceOrderType.ENERGY_AUDIT,
            ].includes(type) && (
              <Button
                variant="info"
                className="btn-sm text-white"
                onClick={() => {
                  history.push(
                    ORDER_DIAGNOSTIC_EDIT.replace(
                      ":id",
                      datedReference || serviceId
                    )
                  );
                }}
              >
                {hasReport ? "Modifier" : "Saisir"} le diagnostic
              </Button>
            )}
            {!isSubmitting && (
              <Button
                disabled={!isDirty}
                variant="primary"
                className="ms-lg-4"
                size="md"
                type={"submit"}
              >
                <i className="pi pi-save me-2" />
                Sauvegarder
              </Button>
            )}
            {hasSuperAdminPermission && (
              <Button
                variant="danger"
                size="md"
                className="ms-lg-4 text-white"
                onClick={() => handleDeletionConfirm()}
              >
                <i className="pi pi-trash me-2" />
                Supprimer
              </Button>
            )}
            {isSubmitting && dirtyFields?.operator && (
              <Alert
                variant="warning"
                className="d-flex gap-2 align-items-center"
              >
                <Spinner
                  as="span"
                  animation="grow"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span>Mise à jour de l'opérateur en cours...</span>
              </Alert>
            )}
            {isSubmitting && dirtyFields?.slug && (
              <Alert
                variant="warning"
                className="d-flex gap-2 align-items-center"
              >
                <Spinner
                  as="span"
                  animation="grow"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span>Mise à jour du status en cours...</span>
              </Alert>
            )}
            {isSubmitting &&
              (dirtyFields?.startDate || dirtyFields?.endDate) && (
                <Alert
                  variant="warning"
                  className="d-flex gap-2 align-items-center"
                >
                  <Spinner
                    as="span"
                    animation="grow"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span>Mise à jour du rendez-vous en cours...</span>
                </Alert>
              )}
          </div>
        </form>
      </div>
      <hr />
      <span className="history-title mb-2">Suivi des interventions : </span>
      <div className="dwelling_interventions">
        <DwellingInterventionHistoryTableComponent
          dwellingId={dwellingId}
          needPagination={false}
        />
      </div>
    </>
  );
};

export default OrderPreview;
